/**
 * ErrorBoundary — top-level React error catch for the SPA.
 *
 * Round 5 Item 7 Tier 1 #3 (2026-06-25). Wraps the whole app shell. When a
 * descendant throws an unhandled error during render OR in a lifecycle hook,
 * this boundary:
 *
 *   1. Catches the throw + sets `hasError=true` so the next render returns
 *      <ServerError /> instead of the white screen.
 *   2. Best-effort POSTs the error info to `/api/client-error-report` which
 *      writes a sys__operator_alert row with pattern='unhandled_500_client'.
 *      Operator (Ean + Josh) sees it via Neon MCP queries.
 *   3. Stamps the returned incident_id into component state so the
 *      ServerError surface can show a "Ref: abc12345..." for support paste.
 *
 * The boundary does NOT auto-recover. The user has to refresh or navigate
 * back home. This is intentional — once a render throws, downstream state
 * is likely corrupt; auto-recovery often makes things worse. The Try Again
 * button on ServerError calls window.location.reload() which IS the clean
 * recovery path.
 *
 * Limitation: React error boundaries do NOT catch errors in event handlers,
 * async code, or during setState in effects. Those failures bubble through
 * existing toast / network failure paths. THIS boundary catches the
 * "render-time blew up" class only.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ServerError } from '../pages/ServerError.tsx';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  incidentId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, incidentId: null };

  static getDerivedStateFromError(_error: Error): Partial<State> {
    // First render after the throw — flip to the fault surface. The actual
    // error reporting happens in componentDidCatch (effect-shaped lifecycle
    // that React batches outside the render pass).
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console emit for local-dev visibility.
    console.error('ErrorBoundary caught a render-time throw:', error, info);

    // Best-effort report. We DON'T await — the fault page already rendered
    // via getDerivedStateFromError, and the user-facing path doesn't depend
    // on whether the report lands.
    const rawError = [
      error.name,
      error.message,
      error.stack ?? '(no stack)',
      info.componentStack ?? '(no component stack)',
    ].join('\n\n');

    void fetch('/api/client-error-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pattern: 'unhandled_500_client',
        raw_error: rawError,
        path: window.location.pathname,
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        if (
          data !== null &&
          typeof data === 'object' &&
          'incident_id' in data &&
          typeof (data as { incident_id: unknown }).incident_id === 'string'
        ) {
          this.setState({
            incidentId: (data as { incident_id: string }).incident_id,
          });
        }
      })
      .catch(() => {
        // Swallow — the boundary already rendered the fault. Network failure
        // here just means the operator alert didn't land; user-facing path
        // unaffected.
      });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ServerError />;
    }
    return this.props.children;
  }
}
